import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Star, Phone, Mail, MapPin, Shield, Zap, Clock, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Main Services Section Component
export default function TradesServicesSection({ 
  subcategorySlug, 
  country = 'UK',
  title,
  showEmergencyOnly = false,
}) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcategoryConfig, setSubcategoryConfig] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showContactDialog, setShowContactDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [providersRes, configRes] = await Promise.all([
          fetch(`${API_URL}/api/trades-services/providers?subcategory=${subcategorySlug}&country=${country}&emergency_only=${showEmergencyOnly}`),
          fetch(`${API_URL}/api/trades-services/config/subcategory/${subcategorySlug}`).catch(() => null),
        ]);

        const providersData = await providersRes.json();
        setProviders(providersData.providers || []);

        if (configRes) {
          const configData = await configRes.json();
          setSubcategoryConfig(configData.subcategory);
        }
      } catch (error) {
        console.error('Failed to fetch trade services:', error);
      } finally {
        setLoading(false);
      }
    };

    if (subcategorySlug) {
      fetchData();
    }
  }, [subcategorySlug, country, showEmergencyOnly]);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading service providers...</p>
      </div>
    );
  }

  if (providers.length === 0) {
    return null;
  }

  const handleContactClick = (provider) => {
    setSelectedProvider(provider);
    setShowContactDialog(true);
  };

  return (
    <div className="py-6">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            {subcategoryConfig?.subcategory_icon || '🔧'}
            {title || subcategoryConfig?.subcategory_name || 'Trade Services'}
          </h2>
          <p className="text-sm text-gray-600">
            Find trusted local professionals
          </p>
        </div>
        <Badge className="bg-blue-100 text-blue-700">
          {providers.length} providers
        </Badge>
      </div>

      {/* Disclaimer if exists */}
      {subcategoryConfig?.disclaimer && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {subcategoryConfig.disclaimer}
          </p>
        </div>
      )}

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((provider) => (
          <TradeProviderCard
            key={provider.id}
            provider={provider}
            onContact={() => handleContactClick(provider)}
            country={country}
          />
        ))}
      </div>

      {/* Contact Dialog */}
      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
        provider={selectedProvider}
      />
    </div>
  );
}

// Trade Provider Card Component
export function TradeProviderCard({ provider, onContact, country = 'UK' }) {
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  const getVerificationBadge = (level) => {
    const badges = {
      1: { label: 'Verified', color: 'bg-blue-100 text-blue-700' },
      2: { label: 'Trusted', color: 'bg-green-100 text-green-700' },
      3: { label: 'Premium', color: 'bg-purple-100 text-purple-700' },
    };
    return badges[level] || null;
  };

  const verificationBadge = getVerificationBadge(provider.verification_level);

  // Generate initials from provider name for fallback
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <Card className="hover:shadow-lg transition-shadow overflow-hidden">
      {/* Banner Image */}
      {provider.banner_url && (
        <div className="h-24 bg-gradient-to-r from-blue-500 to-blue-600 relative">
          <img 
            src={provider.banner_url} 
            alt={`${provider.name} banner`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      
      <CardContent className={`p-4 ${provider.banner_url ? '-mt-8 relative' : ''}`}>
        {/* Header with Logo */}
        <div className="flex items-start gap-3 mb-3">
          {/* Provider Logo */}
          <div className={`flex-shrink-0 ${provider.banner_url ? 'ring-4 ring-white' : ''}`}>
            {provider.logo_url ? (
              <img 
                src={provider.logo_url} 
                alt={`${provider.name} logo`}
                className="w-14 h-14 rounded-lg object-cover bg-white shadow-sm"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`w-14 h-14 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-lg flex items-center justify-center shadow-sm ${provider.logo_url ? 'hidden' : ''}`}
            >
              {getInitials(provider.name)}
            </div>
          </div>

          {/* Name & Business Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg truncate">{provider.name}</h3>
              {provider.emergency_available && (
                <Badge className="bg-red-100 text-red-700 flex-shrink-0">
                  <Zap className="w-3 h-3 mr-1" />
                  Emergency
                </Badge>
              )}
            </div>
            {provider.business_name && (
              <p className="text-sm text-gray-500 truncate">{provider.business_name}</p>
            )}
            {verificationBadge && (
              <Badge className={`${verificationBadge.color} mt-1`}>
                <Shield className="w-3 h-3 mr-1" />
                {verificationBadge.label}
              </Badge>
            )}
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(provider.rating || 0)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-sm font-medium">{provider.rating?.toFixed(1) || '0.0'}</span>
          <span className="text-sm text-gray-500">({provider.review_count || 0} reviews)</span>
        </div>

        {/* Services */}
        <div className="mb-3">
          <p className="text-sm text-gray-600 mb-1">Services:</p>
          <div className="flex flex-wrap gap-1">
            {provider.services_offered?.slice(0, 4).map((service) => (
              <Badge key={service} variant="outline" className="text-xs">
                {service.replace(/_/g, ' ')}
              </Badge>
            ))}
            {provider.services_offered?.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{provider.services_offered.length - 4} more
              </Badge>
            )}
          </div>
        </div>

        {/* Location & Pricing */}
        <div className="flex items-center justify-between text-sm mb-4">
          <div className="flex items-center gap-1 text-gray-600">
            <MapPin className="w-4 h-4" />
            {provider.service_area}
          </div>
          {provider.hourly_rate && (
            <span className="font-semibold text-green-600">
              {currencySymbol}{provider.hourly_rate}/hr
            </span>
          )}
          {provider.pricing_type === 'quote' && !provider.hourly_rate && (
            <span className="text-gray-600">Free quotes</span>
          )}
        </div>

        {/* Action Button */}
        <Button className="w-full" onClick={onContact}>
          <Phone className="w-4 h-4 mr-2" />
          Contact Provider
        </Button>
      </CardContent>
    </Card>
  );
}

// Contact Dialog Component
function ContactDialog({ open, onOpenChange, provider }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In a real app, this would send a message/lead to the provider
    console.log('Contact form submitted:', { provider: provider?.id, ...formData });
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', phone: '', email: '', message: '' });
      onOpenChange(false);
    }, 2000);
  };

  // Generate initials from provider name for fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Contact {provider.name}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <p className="font-semibold text-green-700">Message Sent!</p>
            <p className="text-sm text-gray-600">The provider will contact you shortly.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Provider Quick Info with Logo */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {/* Provider Logo */}
                {provider.logo_url ? (
                  <img 
                    src={provider.logo_url} 
                    alt={`${provider.name} logo`}
                    className="w-12 h-12 rounded-lg object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold flex items-center justify-center ${provider.logo_url ? 'hidden' : ''}`}
                >
                  {getInitials(provider.name)}
                </div>
                
                <div className="flex-1">
                  <p className="font-medium">{provider.name}</p>
                  <p className="text-sm text-gray-500">{provider.service_area}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{provider.rating?.toFixed(1)}</span>
                </div>
              </div>
            </div>

            {/* Direct Contact */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`tel:${provider.contact_phone}`}
                className="flex items-center justify-center gap-2 p-3 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <a
                href={`mailto:${provider.contact_email}`}
                className="flex items-center justify-center gap-2 p-3 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or send a message</span>
              </div>
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Describe what you need help with..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Send Message
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Emergency Services Banner Component
export function EmergencyServicesBanner({ country = 'UK', onViewAll }) {
  const [emergencyProviders, setEmergencyProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmergency = async () => {
      try {
        const res = await fetch(`${API_URL}/api/trades-services/providers?country=${country}&emergency_only=true&limit=3`);
        const data = await res.json();
        setEmergencyProviders(data.providers || []);
      } catch (error) {
        console.error('Failed to fetch emergency providers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmergency();
  }, [country]);

  if (loading || emergencyProviders.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-6 h-6" />
          <h3 className="font-bold text-lg">Emergency Services Available</h3>
        </div>
        <Badge className="bg-white/20 text-white">24/7</Badge>
      </div>
      <p className="text-sm text-red-100 mb-3">
        Need urgent help? These providers offer emergency call-outs.
      </p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {emergencyProviders.map((provider) => (
          <div
            key={provider.id}
            className="flex-shrink-0 bg-white/10 backdrop-blur rounded-lg p-3 min-w-[200px]"
          >
            <p className="font-medium">{provider.name}</p>
            <p className="text-sm text-red-100">{provider.subcategory_slug?.replace(/-/g, ' ')}</p>
            <a
              href={`tel:${provider.contact_phone}`}
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium hover:underline"
            >
              <Phone className="w-3 h-3" />
              Call Now
            </a>
          </div>
        ))}
      </div>
      {onViewAll && (
        <Button
          variant="ghost"
          className="mt-2 text-white hover:bg-white/20"
          onClick={onViewAll}
        >
          View All Emergency Services
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

// Subcategory Grid Component
export function TradesSubcategoryGrid({ country = 'UK', onSelectSubcategory }) {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/trades-services/config/country/${country}`);
        const data = await res.json();
        setSubcategories(data.subcategories || []);
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubcategories();
  }, [country]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {subcategories.map((subcat) => (
        <Card
          key={subcat.slug}
          className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
          onClick={() => onSelectSubcategory?.(subcat.slug)}
        >
          <CardContent className="p-4 text-center">
            <span className="text-3xl mb-2 block">{subcat.subcategory_icon}</span>
            <p className="font-medium text-sm">{subcat.subcategory_name}</p>
            {subcat.is_custom && (
              <Badge className="mt-1 bg-blue-100 text-blue-700 text-xs">
                {subcat.country_only}-only
              </Badge>
            )}
            {subcat.licensed_trade && (
              <Badge className="mt-1 bg-purple-100 text-purple-700 text-xs">
                <Shield className="w-3 h-3 mr-1 inline" />
                Licensed
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// Featured Trade Providers Component (for main services-trades page)
export function FeaturedTradeProviders({ country = 'UK' }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showContactDialog, setShowContactDialog] = useState(false);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      try {
        // Fetch top-rated providers across all subcategories
        const res = await fetch(`${API_URL}/api/trades-services/providers?country=${country}&verified_only=true&limit=6`);
        const data = await res.json();
        setProviders(data.providers || []);
      } catch (error) {
        console.error('Failed to fetch featured providers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProviders();
  }, [country]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-gray-200 animate-pulse rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (providers.length === 0) return null;

  const handleContactClick = (provider) => {
    setSelectedProvider(provider);
    setShowContactDialog(true);
  };

  return (
    <div className="py-6">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            ⭐ Featured Service Providers
          </h2>
          <p className="text-sm text-gray-600">
            Top-rated professionals ready to help
          </p>
        </div>
        <Badge className="bg-blue-100 text-blue-700">
          {providers.length} providers
        </Badge>
      </div>

      {/* Provider Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((provider) => (
          <TradeProviderCard
            key={provider.id}
            provider={provider}
            onContact={() => handleContactClick(provider)}
            country={country}
          />
        ))}
      </div>

      {/* Contact Dialog */}
      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
        provider={selectedProvider}
      />
    </div>
  );
}

